/** Largest `limit` the API accepts on cursor-paginated list endpoints (`components/parameters/Limit`, maximum 200). */
export const API_PAGE_LIMIT = 200;

export type CursorPageOf<T> = { items: T[]; next_cursor?: string | null };

/**
 * Drain a cursor-paginated endpoint into one array. `fetchPage` is called with `undefined` for the first page and
 * then with every `next_cursor` the server returns, until it returns `null`/`undefined` or `max` items are collected.
 * Catalogue hooks (tags, members, people, share links) use it so a workspace larger than one page never truncates and
 * the request never exceeds the server's page cap (which answers 422 instead of clamping).
 */
export async function fetchAllPages<T>(
  fetchPage: (cursor: string | undefined, limit: number) => Promise<CursorPageOf<T>>,
  opts: { pageSize?: number; max?: number } = {},
): Promise<T[]> {
  const pageSize = Math.min(opts.pageSize ?? API_PAGE_LIMIT, API_PAGE_LIMIT);
  const max = opts.max ?? 5000;
  const out: T[] = [];
  let cursor: string | undefined;
  const seen = new Set<string>();
  for (;;) {
    const page = await fetchPage(cursor, pageSize);
    out.push(...page.items);
    const next = page.next_cursor ?? undefined;
    if (!next || page.items.length === 0 || out.length >= max) break;
    if (seen.has(next)) break; // defensive: a server that repeats a cursor must not loop forever
    seen.add(next);
    cursor = next;
  }
  return out.length > max ? out.slice(0, max) : out;
}
